import { useCallback, useEffect, useRef, useState } from 'react';

import { durations } from '@atlaskit/motion/utils/durations';

/** Table heights used for expand/collapse animation; exported for layout calculations in the card. */
export const TABLE_HEIGHT_COLLAPSED = 240;
export const TABLE_HEIGHT_EXPANDED = 495;

const TABLE_RESIZE_DURATION_MS = durations.medium;

/** Cubic ease-in-out for progress 0–1 (matches easeInOut curve feel). */
function easeInOutCubic(t: number): number {
	return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

export type UseJiraNlToJqlCardAnimationResult = {
	isHidden: boolean;
	isExpanded: boolean;
	contentWrapperTransitioning: boolean;
	justRevealed: boolean;
	revealHeightZero: boolean;
	animatedTableHeight: number;
	onHideClick: () => void;
	onRevealClick: () => void;
	onExpandClick: () => void;
	onCollapseClick: () => void;
	onContentWrapperTransitionEnd: (e: React.TransitionEvent) => void;
};

export type UseJiraNlToJqlCardAnimationOptions = {
	/** When true and expanded, keep animatedTableHeight at COLLAPSED so the card can use height:auto; collapse then snaps without jumping to 495. */
	expandToAuto?: boolean;
};

/**
 * Manages hide/reveal and expand/collapse animation state and behaviour for the Jira NL to JQL card.
 * Callers should wrap the click handlers with analytics as needed.
 */
export function useJiraNlToJqlCardAnimation(
	animationsEnabled: boolean,
	options?: UseJiraNlToJqlCardAnimationOptions,
): UseJiraNlToJqlCardAnimationResult {
	const expandToAuto = options?.expandToAuto ?? false;

	const [isHidden, setIsHidden] = useState(false);
	const [isExpanded, setIsExpanded] = useState(false);
	const [contentWrapperTransitioning, setContentWrapperTransitioning] = useState(false);
	const [justRevealed, setJustRevealed] = useState(false);
	const [revealHeightZero, setRevealHeightZero] = useState(false);
	const [animatedTableHeight, setAnimatedTableHeight] = useState(TABLE_HEIGHT_COLLAPSED);

	const tableHeightRef = useRef(TABLE_HEIGHT_COLLAPSED);
	const tableAnimationFrameRef = useRef<number | null>(null);
	tableHeightRef.current = animatedTableHeight;

	// Expand/collapse: when expandToAuto we keep height at COLLAPSED so the card uses height:auto and collapse doesn't jump
	useEffect(() => {
		const expandedTarget =
			expandToAuto && isExpanded ? TABLE_HEIGHT_COLLAPSED : TABLE_HEIGHT_EXPANDED;
		const targetHeight = isExpanded ? expandedTarget : TABLE_HEIGHT_COLLAPSED;
		const startHeight = tableHeightRef.current;
		if (startHeight === targetHeight) {
			return;
		}

		if (!animationsEnabled) {
			tableHeightRef.current = targetHeight;
			setAnimatedTableHeight(targetHeight);
			return;
		}

		if (tableAnimationFrameRef.current !== null) {
			cancelAnimationFrame(tableAnimationFrameRef.current);
		}

		const startTime = performance.now();

		const tick = (now: number) => {
			const elapsed = now - startTime;
			const progress = Math.min(elapsed / TABLE_RESIZE_DURATION_MS, 1);
			const eased = easeInOutCubic(progress);
			const current = Math.round(startHeight + (targetHeight - startHeight) * eased);
			tableHeightRef.current = current;
			setAnimatedTableHeight(current);
			if (progress < 1) {
				tableAnimationFrameRef.current = requestAnimationFrame(tick);
			} else {
				tableAnimationFrameRef.current = null;
			}
		};
		tableAnimationFrameRef.current = requestAnimationFrame(tick);
		return () => {
			if (tableAnimationFrameRef.current !== null) {
				cancelAnimationFrame(tableAnimationFrameRef.current);
				tableAnimationFrameRef.current = null;
			}
		};
	}, [isExpanded, animationsEnabled, expandToAuto]);

	// After reveal, clear both flags in one frame so opacity and height stay in sync
	useEffect(() => {
		if (!justRevealed && !revealHeightZero) {
			return;
		}
		const frameId = requestAnimationFrame(() => {
			setJustRevealed(false);
			setRevealHeightZero(false);
		});
		return () => cancelAnimationFrame(frameId);
	}, [justRevealed, revealHeightZero]);

	const onHideClick = useCallback(() => {
		if (animationsEnabled) {
			setContentWrapperTransitioning(true);
		}
		setIsHidden(true);
	}, [animationsEnabled]);

	const onRevealClick = useCallback(() => {
		if (animationsEnabled) {
			setJustRevealed(true);
			setRevealHeightZero(true);
			setContentWrapperTransitioning(true);
		}
		setIsHidden(false);
	}, [animationsEnabled]);

	const onExpandClick = useCallback(() => {
		setIsExpanded(true);
	}, []);

	const onCollapseClick = useCallback(() => {
		setIsExpanded(false);
	}, []);

	const onContentWrapperTransitionEnd = useCallback((e: React.TransitionEvent) => {
		if (e.propertyName === 'height') {
			setContentWrapperTransitioning(false);
		}
	}, []);

	return {
		isHidden,
		isExpanded,
		contentWrapperTransitioning,
		justRevealed,
		revealHeightZero,
		animatedTableHeight,
		onHideClick,
		onRevealClick,
		onExpandClick,
		onCollapseClick,
		onContentWrapperTransitionEnd,
	};
}
